import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { MainLayout } from "@/components/layout/MainLayout";
import { ProfileTab, ProfileTabData } from "@/components/generator/ProfileTab";
import { MessageConfig } from "@/components/generator/MessageConfig";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { SectionCard } from "@/components/ui/section-card";
import { toast } from "@/hooks/use-toast";
import { authFetch } from "@/lib/auth";
import { projectsApi, linkedinApi } from "@/lib/api";
import { useProfileTabs } from "@/contexts/ProfileTabsContext";
import { X, Search, ExternalLink, Loader2, Check, AlertCircle, User, Code } from "lucide-react";
import type { ScrapedData, MessageRequest, Sender, GenerationState } from "@/types/api";
import type { DevModeData } from "@/components/generator/DevModePanel";

interface ProjectData {
  id: number;
  name: string;
  prompt_id: string | null;
}

const defaultConfig: Partial<MessageRequest> = {
  project_id: 0,
  project_name: "",
  scenario: "sales-classic",
  llm_provider: "gemini",
  message_type: "invitation",
  message_length: "medium",
  formality_level: "semiformal",
  language: "pl",
  writing_style: "service-listing",
  sex: "male",
  cta_option: "none",
  prompt_id: "",
  activity_reference: false,
  research_mode: true,
  research_country: "Poland",
} as Partial<MessageRequest>;

const extractProfileName = (url: string): string => {
  const linkedInMatch = url.match(/linkedin\.com\/in\/([^\/\?]+)/);
  if (linkedInMatch) return linkedInMatch[1];
  return url.replace(/[\/\?#].*/g, '').trim() || 'profil';
};

const Index = () => {
  const { t } = useTranslation('generator');
  const { t: tc } = useTranslation('common');
  const { 
    profileTabs, activeTabId, devModeEnabled, 
    addProfileTab, removeProfileTab, updateProfileTab, 
    setActiveTabId, setDevModeEnabled,
    fetchOrGetCachedProject,
  } = useProfileTabs();

  const [profileUrl, setProfileUrl] = useState("");
  const [activityReference, setActivityReference] = useState(false);
  const [config, setConfig] = useState<Partial<MessageRequest>>(defaultConfig);
  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [senders, setSenders] = useState<Sender[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [isLoadingSenders, setIsLoadingSenders] = useState(false);
  const [invalidLinks, setInvalidLinks] = useState<string[]>([]);

  const fetchProjects = useCallback(async () => {
    setIsLoadingProjects(true);
    try {
      const response = await authFetch(
        `${projectsApi('action=list_collections')}`
      );
      if (!response.ok) throw new Error(`Failed to fetch projects: ${response.status}`);
      const data = await response.json();
      const collections = data.collections || data || [];
      const projectsList: ProjectData[] = Array.isArray(collections)
        ? collections
            .filter((col: any) => {
              const name = typeof col === 'string' ? col : (col.name || col.collection_name);
              return name !== 'Generic';
            })
            .map((col: any, index: number) => ({
              id: typeof col === 'string' ? index + 1 : (col.id || index + 1),
              name: typeof col === 'string' ? col : (col.name || col.collection_name),
              prompt_id: typeof col === 'string' ? null : (col.prompt_id || null),
            }))
        : [];
      setProjects(projectsList);
      if (projectsList.length > 0 && !config.project_name) {
        const firstProject = projectsList[0];
        setConfig(prev => ({ ...prev, project_name: firstProject.name }));
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast({ title: tc('error'), description: t('toast.errorFetchProjects'), variant: "destructive" });
    } finally {
      setIsLoadingProjects(false);
    }
  }, []);

  const fetchProjectDetails = useCallback(async (projectName: string) => {
    if (!projectName) return;
    setIsLoadingSenders(true);
    try {
      const cached = await fetchOrGetCachedProject(projectName);
      if (cached) {
        setConfig(prev => ({ ...prev, project_id: cached.projectId, prompt_id: cached.promptId || '143430' }));
        setSenders(cached.senders);
        if (cached.senders.length === 1) {
          const singleSender = cached.senders[0];
          setConfig(prev => ({ ...prev, sender: `${singleSender.name} ${singleSender.surname}` }));
        } else {
          setConfig(prev => {
            const currentSenderExists = cached.senders.some(
              s => `${s.name} ${s.surname}` === prev.sender
            );
            return currentSenderExists ? prev : { ...prev, sender: undefined };
          });
        }
      } else {
        setSenders([]);
      }
    } finally {
      setIsLoadingSenders(false);
    }
  }, [fetchOrGetCachedProject]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  useEffect(() => { if (config.project_name) fetchProjectDetails(config.project_name); }, [config.project_name, fetchProjectDetails]);

  // Global keyboard shortcut: Cmd/Ctrl + Shift + Enter → trigger mass-generate package
  // on the currently active beta ChatGenerator (and all listeners across tabs).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter" && e.shiftKey && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent("sh:mass-generate-package"));
        toast({ title: "Pakiet", description: "Uruchomiono generowanie pakietu (Cmd/Ctrl+Shift+Enter)" });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleAddProfile = () => {
    if (!profileUrl.trim()) {
      toast({ title: tc('error'), description: t('scanner.errorEmptyUrl'), variant: "destructive" });
      return;
    }

    const rawLinks = profileUrl.split(/[,\n\s]+/).map(s => s.trim()).filter(Boolean);
    
    if (rawLinks.length > 20) {
      toast({ title: tc('error'), description: t('scanner.maxLinksExceeded', { max: 20 }), variant: "destructive" });
    }

    const linksToProcess = rawLinks.slice(0, 20);
    const validUrls: string[] = [];
    const invalidUrls: string[] = [];

    for (const link of linksToProcess) {
      if (link.includes('linkedin.com/in/')) {
        const normalized = link.startsWith('http') ? link : `https://${link}`;
        validUrls.push(normalized);
      } else {
        invalidUrls.push(link);
      }
    }

    if (validUrls.length === 0) {
      toast({ title: tc('error'), description: t('scanner.noValidLinks'), variant: "destructive" });
      return;
    }

    const initialTabConfig = { ...config, activity_reference: activityReference };
    const now = Date.now();

    validUrls.forEach((url, index) => {
      const profileName = extractProfileName(url);
      const tabId = `tab-${now}-${index}`;
      const newTab: ProfileTabData = {
        id: tabId, profileUrl: url, profileName, status: 'scraping',
        activityReference, initialConfig: initialTabConfig, config: initialTabConfig,
        generationState: { status: "idle" },
      };
      addProfileTab(newTab);
      scrapeProfile(
        tabId,
        url,
        activityReference,
        Boolean((config as any).research_mode),
        ((config as any).research_country as string) || 'Poland',
      );
    });

    setProfileUrl("");
    toast({ title: t('scanner.profileAdded'), description: t('scanner.profilesAddedCount', { count: validUrls.length }) });

    if (invalidUrls.length > 0) {
      setInvalidLinks(prev => [...prev, ...invalidUrls]);
      toast({
        title: tc('error'),
        description: t('scanner.invalidLinks', { count: invalidUrls.length, links: invalidUrls.join(', ') }),
        variant: "destructive",
      });
    }
  };

  const scrapeProfile = async (
    tabId: string,
    url: string,
    withPosts: boolean,
    researchMode: boolean = false,
    researchCountry: string = 'Poland',
  ) => {
    try {
      const response = await authFetch(linkedinApi(), {
        method: 'POST',
        body: JSON.stringify({
          profileUrl: url,
          activityReference: withPosts,
          research_mode: researchMode,
          research_country: researchCountry,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        updateProfileTab(tabId, { status: 'error', error: data?.error || t('profileTab.scrapingError') });
        return;
      }
      const { profile, profileDataText, scrapedAt, posts, research, research_error } = data.data;
      const scrapedData: ScrapedData = {
        id: Date.now().toString(),
        profile,
        scrapedAt,
        profileDataText,
        posts: posts || [],
        ...(research
          ? { research: { ...research } }
          : research_error && researchMode
            ? { research: { context: '', country: researchCountry, fetched_at: new Date().toISOString(), error: research_error } }
            : {}),
      };
      updateProfileTab(tabId, {
        status: 'ready', scrapedData,
        profileName: profile.name || profileTabs.find(t => t.id === tabId)?.profileName,
      });
      toast({
        title: tc('success'),
        description: posts?.length
          ? t('profileTab.successProfileWithPosts', { name: profile.name, count: posts.length })
          : t('profileTab.successProfile', { name: profile.name }),
      });
      if (researchMode) {
        if (research?.context) {
          toast({
            title: 'Research firmy',
            description: research.cached ? 'Załadowano z cache.' : 'Pobrano świeże dane o firmie.',
          });
        } else if (research_error) {
          toast({
            title: 'Research firmy',
            description: `Profil pobrany, ale research firmy się nie powiódł: ${research_error}`,
            variant: 'destructive',
          });
        }
      }
    } catch (error) {
      console.error('Scrape error:', error);
      updateProfileTab(tabId, { status: 'error', error: t('profileTab.scrapingError') });
    }
  };

  const handleCloseTab = (tabId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (tabId === '__invalid__') {
      setInvalidLinks([]);
      if (activeTabId === '__invalid__') setActiveTabId(null);
      return;
    }
    removeProfileTab(tabId);
  };

  const handleTabConfigChange = (
    tabId: string,
    newConfig: Partial<MessageRequest> | ((prev: Partial<MessageRequest>) => Partial<MessageRequest>)
  ) => {
    updateProfileTab(tabId, prev => ({
      config: typeof newConfig === 'function'
        ? (newConfig as (p: Partial<MessageRequest>) => Partial<MessageRequest>)(prev.config)
        : newConfig,
    }));
  };

  const handleTabGenerationStateChange = (tabId: string, newState: GenerationState) => {
    updateProfileTab(tabId, { generationState: newState });
  };

  const handleDevModeDataChange = (tabId: string, data: DevModeData) => {
    updateProfileTab(tabId, { devModeData: data });
  };

  const handleChatStateChange = (tabId: string, chatState: ProfileTabData['chatState']) => {
    updateProfileTab(tabId, { chatState });
  };


  const activeTab = profileTabs.find(tab => tab.id === activeTabId);

  return (
    <MainLayout>
      <div className="mb-6 flex items-center gap-2 border-b border-border pb-2 overflow-x-auto">
        <button
          onClick={() => setActiveTabId(null)}
          className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap ${
            activeTabId === null
              ? 'bg-primary text-primary-foreground'
              : 'bg-secondary/50 hover:bg-secondary text-foreground'
          }`}
        >
          <ExternalLink className="h-4 w-4" />
          {t('tabs.generator')}
        </button>

        {invalidLinks.length > 0 && (
          <button
            onClick={() => setActiveTabId('__invalid__')}
            className={`group flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeTabId === '__invalid__'
                ? 'bg-destructive text-destructive-foreground'
                : 'bg-destructive/10 hover:bg-destructive/20 text-destructive'
            }`}
          >
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{t('tabs.invalidProfiles')}</span>
            <span className="ml-1 text-xs bg-destructive/20 px-1.5 py-0.5 rounded-full">{invalidLinks.length}</span>
            <button
              onClick={(e) => handleCloseTab('__invalid__', e)}
              className={`ml-1 p-0.5 rounded hover:bg-background/20 flex-shrink-0 ${
                activeTabId === '__invalid__' ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
              }`}
            >
              <X className="h-3 w-3" />
            </button>
          </button>
        )}

        {profileTabs.map(tab => {
          const isGenerating = tab.generationState?.status === 'pending';
          const isLoading = tab.status === 'scraping' || isGenerating;
          const hasGeneratedResult = Boolean(tab.generationState?.result?.generated_message?.trim());
          const hasChatAssistantMessage = Boolean(
            tab.chatState && Object.values(tab.chatState).some(roleState =>
              roleState.history.some(message => message.role === 'assistant' && message.content.trim())
            )
          );
          const isSuccess = tab.status === 'ready'
            && tab.generationState?.status === 'success'
            && (hasGeneratedResult || hasChatAssistantMessage);
          const isError = tab.status === 'error';
          const isReady = tab.status === 'ready' && !isSuccess;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTabId(tab.id)}
              className={`group flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap max-w-[200px] ${
                activeTabId === tab.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary/50 hover:bg-secondary text-foreground'
              }`}
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
                : isError ? <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                : isSuccess ? <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                : isReady ? <span className="h-2 w-2 rounded-full bg-yellow-500 flex-shrink-0" />
                : <User className="h-4 w-4 flex-shrink-0" />}
              <span className="truncate">{tab.profileName}</span>
              <button
                onClick={(e) => handleCloseTab(tab.id, e)}
                className={`ml-1 p-0.5 rounded hover:bg-background/20 flex-shrink-0 ${
                  activeTabId === tab.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                }`}
              >
                <X className="h-3 w-3" />
              </button>
            </button>
          );
        })}
      </div>

      {activeTabId === null ? (
        <div className="grid gap-6 lg:grid-cols-2">
          <div>
            <SectionCard
              icon={ExternalLink}
              title={t('scanner.title')}
              description={activityReference ? t('scanner.descriptionWithPosts') : t('scanner.description')}
            >
              <div className="space-y-4">
                <div className="flex gap-2 items-start">
                  <Textarea
                    placeholder={t('scanner.placeholderMulti')}
                    value={profileUrl}
                    onChange={(e) => setProfileUrl(e.target.value)}
                    className="flex-1 min-h-[80px] resize-none"
                    rows={3}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        handleAddProfile();
                      }
                    }}
                  />
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-secondary/20">
                    <Switch id="activity-ref" checked={activityReference} onCheckedChange={setActivityReference} />
                    <Label htmlFor="activity-ref" className="text-xs whitespace-nowrap cursor-pointer">
                      {t('scanner.posts')}
                    </Label>
                  </div>
                  <Button onClick={handleAddProfile}>
                    <Search className="mr-2 h-4 w-4" />
                    {t('scanner.fetch')}
                  </Button>
                </div>

                {profileTabs.length > 0 && (
                  <div className="text-sm text-muted-foreground text-center py-4 border-t border-border">
                    {t('scanner.openProfiles_many', { count: profileTabs.length })} {t('scanner.clickTabHint')}
                  </div>
                )}

                {profileTabs.length === 0 && (
                  <div className="text-sm text-muted-foreground text-center py-4 border-t border-border">
                    <p>{t('scanner.emptyHint')}</p>
                    <p className="mt-1">{t('scanner.emptyHint2')}</p>
                  </div>
                )}
              </div>
            </SectionCard>
          </div>

          <div>
            <MessageConfig
              config={config}
              onConfigChange={setConfig}
              projects={projects}
              senders={senders}
              isLoadingProjects={isLoadingProjects}
              isLoadingSenders={isLoadingSenders}
              isGenerating={false}
              onGenerate={() => {
                toast({ title: t('toast.infoTitle'), description: t('toast.fetchProfileFirst') });
              }}
              onGenerateAudio={() => {}}
              onReset={() => setConfig(defaultConfig)}
              hideGenerateButtons
            />
          </div>
        </div>
      ) : activeTabId === '__invalid__' ? (
        <SectionCard
          icon={AlertCircle}
          title={t('tabs.invalidProfiles')}
          description={t('scanner.invalidProfilesDescription')}
          actions={
            <Button variant="outline" size="sm" onClick={() => { setInvalidLinks([]); setActiveTabId(null); }}>
              {t('scanner.clearInvalidList')}
            </Button>
          }
        >
          <ul className="space-y-2">
            {invalidLinks.map((link, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-destructive bg-destructive/5 rounded-md px-3 py-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span className="break-all">{link}</span>
              </li>
            ))}
          </ul>
        </SectionCard>
      ) : activeTab ? (
        <ProfileTab
          key={activeTab.id}
          tabData={activeTab}
          projects={projects}
          devModeEnabled={devModeEnabled}
          onConfigChange={handleTabConfigChange}
          onGenerationStateChange={handleTabGenerationStateChange}
          onDevModeDataChange={handleDevModeDataChange}
          onChatStateChange={handleChatStateChange}

        />
      ) : null}

      <div className="mt-8 pt-4 border-t border-border flex items-center justify-end gap-3">
        <Code className="h-4 w-4 text-muted-foreground" />
        <Label htmlFor="dev-mode" className="text-sm text-muted-foreground cursor-pointer">
          {t('tabs.devMode')}
        </Label>
        <Switch id="dev-mode" checked={devModeEnabled} onCheckedChange={setDevModeEnabled} />
      </div>
    </MainLayout>
  );
};

export default Index;
